import { WebMercatorViewport } from "@deck.gl/core";
import classnames from "classnames";
import { useCallback, useMemo, useRef, useState } from "react";
import { Map as MapGL, Marker, type MapRef } from "react-map-gl/mapbox";

import { DeckGLOverlay } from "@/components/deckgl/deckgl-overlay";
import { accountOwnedMapStyleUrl, knownMapboxStyles } from "@/lib/mapbox/base-layer-styles";
import useMapViewState from "@/features/shared/widgets/map/hooks/useMapViewState";
import {
  chanceStripeWorldScale,
  riskPictureItemPosition,
  riskPictureLayers,
  riskPictureMarkerZIndex,
} from "@/features/shared/widgets/risk-picture/risk-picture-layers";
import { RiskPictureMapMarkerStackIcon } from "@/features/shared/widgets/risk-picture/risk-picture-map-marker";
import {
  groupRiskPictureThumbMarkers,
  RISK_PICTURE_THUMB_MARKER_SCALE,
} from "@/features/shared/widgets/risk-picture/risk-picture-marker-groups";

import type { RiskPictureDay, RiskPictureModel } from "./risk-picture-model";
import styles from "./risk-picture.module.css";

const THUMB_BOUNDS = [
  { lat: 53.57885, lng: 3.2853652 },
  { lat: 51, lng: 7.279572 },
];

interface RiskPictureMapThumbProps {
  day: RiskPictureDay;
  selected: boolean;
  onSelect: () => void;
}

/**
 * Renders a non-interactive map once, then keeps the canvas snapshot as the
 * background so eight live WebGL maps are not kept busy.
 */
const RiskPictureMapThumb = ({ day, selected, onSelect }: RiskPictureMapThumbProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapRef>(null);
  const [snapshot, setSnapshot] = useState<string | undefined>(undefined);

  const viewStateOptions = useMemo(
    () => ({ containerRef, initialCoords: THUMB_BOUNDS, refitOnResize: true }),
    [containerRef.current],
  );
  const { mapViewState } = useMapViewState(viewStateOptions);

  const stripeScale = chanceStripeWorldScale(mapViewState?.zoom);
  const layers = useMemo(
    () =>
      riskPictureLayers(
        day.items,
        `thumb-${day.date}`,
        undefined,
        mapViewState?.zoom,
        false,
      ),
    [day, mapViewState?.zoom, stripeScale],
  );
  const markerGroups = useMemo(() => {
    if (!mapViewState) return [];
    const viewport = new WebMercatorViewport(mapViewState);
    return groupRiskPictureThumbMarkers(day.items, (item) => {
      const [x, y] = viewport.project(riskPictureItemPosition(item));
      return { x, y };
    });
  }, [day.items, mapViewState]);

  const handleRender = useCallback(() => {
    setSnapshot(mapRef.current?.getCanvas()?.toDataURL());
  }, []);

  return (
    <div
      ref={containerRef}
      className={classnames(styles.thumb, selected && styles.thumbSelected)}
      style={snapshot ? { backgroundImage: `url("${snapshot}")` } : undefined}
      onClick={onSelect}
    >
      {mapViewState && (
        <MapGL
          // @ts-expect-error DeckGl MapViewState does not match perfectly
          viewState={mapViewState}
          attributionControl={false}
          projection={{ name: "mercator" }}
          mapStyle={accountOwnedMapStyleUrl(knownMapboxStyles.MonochromeLightWithoutLabels)}
          mapboxAccessToken={import.meta.env.VITE_MAP_ACCESS_TOKEN}
          maxPitch={0}
          bearingSnap={360}
          bearing={0}
          interactive={false}
          onRender={handleRender}
          ref={mapRef}
        >
          <DeckGLOverlay interleaved layers={layers} />
          {markerGroups.map((group) => {
            const top = group.items[0];
            const [longitude, latitude] = riskPictureItemPosition(top);
            return (
              <Marker
                key={group.id}
                longitude={longitude}
                latitude={latitude}
                anchor="center"
                style={{
                  zIndex: riskPictureMarkerZIndex(top, false),
                  pointerEvents: "none",
                }}
              >
                <div
                  className={styles.thumbMarker}
                  style={{ transform: `scale(${RISK_PICTURE_THUMB_MARKER_SCALE})` }}
                >
                  <span className={styles.markerClusterStack}>
                    <RiskPictureMapMarkerStackIcon item={top} count={group.items.length} />
                  </span>
                </div>
              </Marker>
            );
          })}
        </MapGL>
      )}
    </div>
  );
};

interface RiskPictureMapRowProps {
  model: RiskPictureModel;
  selectedDay: number;
  onSelectDay: (index: number) => void;
}

/** Table row of per-day map thumbnails, aligned with the day columns above it. */
export const RiskPictureMapRow = ({
  model,
  selectedDay,
  onSelectDay,
}: RiskPictureMapRowProps) => (
  <tr>
    <td />
    {model.days.map((day, index) => (
      <td key={day.date} className={styles.thumbCell}>
        <RiskPictureMapThumb
          day={day}
          selected={index === selectedDay}
          onSelect={() => onSelectDay(index)}
        />
      </td>
    ))}
  </tr>
);
